import angular from 'angular'

import Clipboard from 'clipboard'
import blankshield from 'blankshield'

import envRaw from '../utils/env-raw'
import StrictObj from '../utils/strict-obj'
import extractRawElm from '../utils/extract-raw-elm'
import attachGetters from '../utils/attach-getters'
import attachSetters from '../utils/attach-setters'
import attachEmitters from '../utils/attach-emitters'

import { makeOR, makeEQ } from '../utils/bits'
import { offClick, onClickPassive } from '../utils/on-click'
import { wrap, partial, uniqueId, constant } from 'lodash-es'

import Template from './gallery-sharing.component.ejs'
import IDLE_ICON from '../../img/sharing-icons/idle-toggle.svg'
import ACTIVE_ICON from '../../img/sharing-icons/active-toggle.svg'
import CLIPBOARD_ICON from '../../img/sharing-icons/clipboard.svg'

angular
  .module('ggs.gallery-sharing')
  .component('ggsGallerySharing', {
    template: Template(StrictObj({
      LE: envRaw.liveEditMode,
      IDLE_ICON,
      ACTIVE_ICON,
      CLIPBOARD_ICON
    })),
    controller: GallerySharingComponent,
    bindings: {
      ggsPost: '<',
      ggsProperties: '<',
      ggsSlideIndex: '<',
      ggsIsTinyLayout: '<',
      ggsSharingAttachCallback: '<'
    }
  })

const STATUS = StrictObj({
  TOOLTIPS_CLOSED: uniqueId('tooltips_closed'),
  TOOLTIP_ICONS_OPEN: uniqueId('tooltip_icons_open'),
  TOOLTIP_WIZARD_OPEN: uniqueId('tooltip_wizard_open')
})

const QS_TOOLTIP_WIZARD = '.ggs-gallery-sharing__tooltip_wizard'
const QS_TOOLTIP_ICONS = '.ggs-gallery-sharing__tooltip_icons'
const QS_TOGGLE_ACTIVE = '.ggs-gallery-sharing__toggle_active'
const QS_TOGGLE_IDLE = '.ggs-gallery-sharing__toggle_idle'
const QS_LINK_COPIED = '.ggs-gallery-sharing__link-copied'
const QS_LINK_ICON = '.ggs-gallery-sharing__icon_link'

const CLICK_EVENT = envRaw.gallerySharingClickEvent

function GallerySharingComponent(
  $log,
  $scope,
  $element,
  qh,
  domh,
  animate,
  postShare,
  StayInQueue,
  LazyResource,
  DefineMethod,
  DefineHandler,
  shareToSocial) {

  $scope.CLICK_EVENT = CLICK_EVENT

  const sign = `gallerySharing[uid=${uniqueId()}]`
  const $ctrl = $scope.$ctrl = attachGetters(attachSetters(StrictObj(this)))
  $ctrl.wrapH = DefineHandler(sign)
  $ctrl.wrapM = DefineMethod(sign)
  const stayInQueue = StayInQueue(sign)

  const tooltipWizardElm = domh.find($element, QS_TOOLTIP_WIZARD)
  const tooltipIconsElm = domh.find($element, QS_TOOLTIP_ICONS)
  const toggleActiveElm = domh.find($element, QS_TOGGLE_ACTIVE)
  const toggleIdleElm = domh.find($element, QS_TOGGLE_IDLE)
  const linkCopiedElm = domh.find($element, QS_LINK_COPIED)

  $ctrl.status = STATUS.TOOLTIPS_CLOSED

  animate.hideForce(tooltipWizardElm)
  animate.hideForce(tooltipIconsElm)
  animate.hideForce(toggleActiveElm)
  animate.showForce(toggleIdleElm)
  animate.hideForce(linkCopiedElm)

  $ctrl.getShareText = partial(postShare.fmtText, $ctrl.ggsProperties)
  $ctrl.getShareLink = partial(postShare.fmtLink, $ctrl.ggsPost, $ctrl.ggsProperties, $ctrl.getters.ggsSlideIndex)

  const linkClipboard = new Clipboard(domh.find($element, QS_LINK_ICON), {
    text: $ctrl.getShareLink,
    container: extractRawElm($element)
  })

  $ctrl.isTinyLayout = attachEmitters(
    $ctrl.ggsIsTinyLayout,
    $ctrl.ggsProperties.key2emitter.sharing_tooltip_force_tiny,
    makeOR(
      $ctrl.ggsIsTinyLayout,
      $ctrl.ggsProperties.key2get.sharing_tooltip_force_tiny))

  const extCodeWizard = LazyResource('reset')

  $ctrl.wrapH('codeWizardAttachCallback', (extCodeWizardReset) => {
    extCodeWizard.reset.replace(extCodeWizardReset)
  })

  // status related things

  $ctrl.wrapM('applyStatus', (newStatus) => {
    if (newStatus === $ctrl.status) {
      return
    }

    const oldStatus = $ctrl.status
    $ctrl.status = newStatus

    if (oldStatus === STATUS.TOOLTIPS_CLOSED) {
      if (newStatus === STATUS.TOOLTIP_ICONS_OPEN) {
        return qh.join(
          $ctrl.forceHideTooltipWizard(),
          $ctrl.forceHideLinkCopied(),
          $ctrl.showTooltipIcons(),
          $ctrl.showToggleActive(),
          $ctrl.hideToggleIdle())
      }
    } else if (oldStatus === STATUS.TOOLTIP_ICONS_OPEN) {
      if (newStatus === STATUS.TOOLTIPS_CLOSED) {
        return qh.join(
          $ctrl.hideTooltipIcons(),
          $ctrl.hideToggleActive(),
          $ctrl.showToggleIdle())
      } else if (newStatus === STATUS.TOOLTIP_WIZARD_OPEN) {
        return qh.join(
          extCodeWizard.reset(),
          $ctrl.showTooltipWizard(),
          $ctrl.hideTooltipIcons())
      }
    } else if (oldStatus === STATUS.TOOLTIP_WIZARD_OPEN) {
      if (newStatus === STATUS.TOOLTIPS_CLOSED) {
        return qh.join(
          $ctrl.hideTooltipWizard(),
          $ctrl.hideToggleActive(),
          $ctrl.showToggleIdle())
      } else if (newStatus === STATUS.TOOLTIP_ICONS_OPEN) {
        return qh.join(
          $ctrl.showTooltipIcons(),
          $ctrl.hideTooltipWizard())
      }
    }

    $log.warn(`${sign}#applyStatus: "${oldStatus}" -> "${newStatus}" status change does not require any actions!`)
  })

  $ctrl.wrapM('waitUser', qh.makeDelay(1e3))

  $ctrl.wrapM('forceHideTooltipWizard', animate.makeHideForce(tooltipWizardElm))
  $ctrl.wrapM('showTooltipWizard', animate.makeShow(tooltipWizardElm))
  $ctrl.wrapM('hideTooltipWizard', animate.makeHide(tooltipWizardElm))

  $ctrl.wrapM('showTooltipIcons', animate.makeShow(tooltipIconsElm))
  $ctrl.wrapM('hideTooltipIcons', animate.makeHide(tooltipIconsElm))

  $ctrl.wrapM('forceHideLinkCopied', animate.makeHideForce(linkCopiedElm))
  $ctrl.wrapM('showLinkCopied', animate.makeShow(linkCopiedElm))

  $ctrl.wrapM('showToggleActive', animate.makeShowForce(toggleActiveElm))
  $ctrl.wrapM('hideToggleActive', animate.makeHideForce(toggleActiveElm))

  $ctrl.wrapM('showToggleIdle', animate.makeShowForce(toggleIdleElm))
  $ctrl.wrapM('hideToggleIdle', animate.makeHideForce(toggleIdleElm))

  // applyStatus wrappers

  $ctrl.wrapM('closeTooltips', wrap(STATUS.TOOLTIPS_CLOSED, $ctrl.applyStatus))
  $ctrl.wrapM('openTooltipIcons', wrap(STATUS.TOOLTIP_ICONS_OPEN, $ctrl.applyStatus))
  $ctrl.wrapM('openTooltipWizard', wrap(STATUS.TOOLTIP_WIZARD_OPEN, $ctrl.applyStatus))

  $ctrl.wrapM('toggleTooltipIcons',
    qh.makeIfElse(
      makeEQ(
        $ctrl.getters.status,
        constant(STATUS.TOOLTIPS_CLOSED)),
      $ctrl.openTooltipIcons,
      $ctrl.closeTooltips))

  // tooltip related handlers and ext-method

  $ctrl.wrapM('extSharingHideTooltips',
    stayInQueue.makeIfIdle(
      $ctrl.closeTooltips))

  $ctrl.wrapH('hideTooltipsRequestHandler',
    stayInQueue.makeIfIdle(
      $ctrl.closeTooltips))

  $ctrl.wrapH('toggleTooltipIconsRequestHandler',
    stayInQueue.makeIfIdle(
      $ctrl.toggleTooltipIcons))

  // copy-link-button related handler

  $ctrl.wrapM('shareToSocial', (provider) => {
    return shareToSocial(
      provider,
      $ctrl.getShareLink(),
      $ctrl.getShareText(),
      $ctrl.ggsPost.preview.file_thumb)
  })

  $ctrl.wrapM('openShareLink', () => {
    return blankshield.open($ctrl.getShareLink())
  })

  $ctrl.wrapH('socialClickHandler',
    stayInQueue.makeIfIdle(
      qh.makeOver(
        $ctrl.shareToSocial,
        $ctrl.closeTooltips)))

  $ctrl.wrapH('clipboardSuccessHandler',
    stayInQueue.makeIfIdle(
      qh.makeOver(
        $ctrl.showLinkCopied,
        $ctrl.waitUser,
        $ctrl.closeTooltips)))

  $ctrl.wrapH('clipboardProblemHandler',
    stayInQueue.makeIfIdle(
      qh.makeOver(
        $ctrl.openShareLink,
        $ctrl.closeTooltips)))

  // code-wizard related handlers

  $ctrl.wrapH('codeClickHandler',
    stayInQueue.makeIfIdle(
      $ctrl.openTooltipWizard))

  $ctrl.wrapH('codeWizardCompleteHandler',
    stayInQueue.makeIfIdle(
      $ctrl.closeTooltips))

  $ctrl.wrapH('codeWizardDiscardHandler',
    stayInQueue.makeIfIdle(
      $ctrl.openTooltipIcons))

  // lifecycle handlers

  $ctrl.wrapH('$onDestroy', () => {
    linkClipboard.off('success', $ctrl.clipboardSuccessHandler)
    linkClipboard.off('error', $ctrl.clipboardProblemHandler)
    linkClipboard.destroy()
    offClick(window, CLICK_EVENT, $ctrl.hideTooltipsRequestHandler)
  })

  $ctrl.wrapH('$onInit', () => {
    linkClipboard.on('success', $ctrl.clipboardSuccessHandler)
    linkClipboard.on('error', $ctrl.clipboardProblemHandler)
    onClickPassive(window, CLICK_EVENT, $ctrl.hideTooltipsRequestHandler)
    return $ctrl.ggsSharingAttachCallback($ctrl.extSharingHideTooltips)
  })
}
